import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLSvg = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Images & Graphics</div>
                <h1>Inline SVG & Vector Graphics</h1>
                <p className="hero-lead">
                    Master Scalable Vector Graphics for crisp, resolution-independent images. Learn to create,
                    style, animate, and optimize SVG for modern web applications.
                </p>
            </div>

            <div className="content-section">
                <h2>What is SVG?</h2>
                <p>
                    <strong>SVG (Scalable Vector Graphics)</strong> is an XML-based vector image format that
                    scales perfectly to any size without losing quality. Unlike raster images (JPEG, PNG),
                    SVG defines shapes mathematically.
                </p>

                <div className="svg-benefits">
                    <h3>Why Use SVG?</h3>
                    <ul>
                        <li><strong>Scalable:</strong> Perfect quality at any size (no pixelation)</li>
                        <li><strong>Small file size:</strong> Especially for simple graphics</li>
                        <li><strong>Editable:</strong> Modify with CSS and JavaScript</li>
                        <li><strong>Accessible:</strong> Text remains searchable and screen-reader friendly</li>
                        <li><strong>Animatable:</strong> CSS and JavaScript animations</li>
                        <li><strong>SEO-friendly:</strong> Text content is indexable</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Basic SVG Structure</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
    <!-- SVG content goes here --&gt;
</svg>

<!-- Key attributes: --&gt;
<!-- width/height: Display size (can use CSS units) --&gt;
<!-- viewBox: "x y width height" - defines coordinate system --&gt;
<!-- xmlns: XML namespace (required for validation) --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Basic Shapes</h2>

                <h3>1. Rectangle</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="100">
    <!-- Basic rectangle --&gt;
    <rect x="10" y="10" width="180" height="80" fill="blue" />
    
    <!-- Rounded corners --&gt;
    <rect x="10" y="10" width="100" height="80" 
          rx="10" ry="10" 
          fill="green" stroke="black" stroke-width="2" />
</svg>`} />
                </div>

                <h3>2. Circle</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="200">
    <circle cx="100" cy="100" r="50" fill="red" />
    
    <!-- cx, cy: center coordinates --&gt;
    <!-- r: radius --&gt;
</svg>`} />
                </div>

                <h3>3. Ellipse</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="150">
    <ellipse cx="100" cy="75" rx="80" ry="50" fill="purple" />
    
    <!-- rx: horizontal radius --&gt;
    <!-- ry: vertical radius --&gt;
</svg>`} />
                </div>

                <h3>4. Line</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="100">
    <line x1="10" y1="10" x2="190" y2="90" 
          stroke="black" stroke-width="2" />
    
    <!-- (x1,y1): start point --&gt;
    <!-- (x2,y2): end point --&gt;
</svg>`} />
                </div>

                <h3>5. Polyline</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="150">
    <polyline points="10,10 50,50 90,30 130,70 170,40" 
              fill="none" stroke="blue" stroke-width="2" />
    
    <!-- Connects multiple points (not closed) --&gt;
</svg>`} />
                </div>

                <h3>6. Polygon</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="200">
    <!-- Triangle --&gt;
    <polygon points="100,20 20,180 180,180" fill="orange" />
    
    <!-- Automatically closes path --&gt;
</svg>`} />
                </div>

                <h3>7. Path (Most Powerful)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="200">
    <path d="M 10 10 L 190 10 L 100 190 Z" 
          fill="green" stroke="black" stroke-width="2" />
    
    <!-- Path commands: --&gt;
    <!-- M: Move to --&gt;
    <!-- L: Line to --&gt;
    <!-- H: Horizontal line --&gt;
    <!-- V: Vertical line --&gt;
    <!-- C: Cubic Bzier curve --&gt;
    <!-- Q: Quadratic Bzier curve --&gt;
    <!-- A: Arc --&gt;
    <!-- Z: Close path --&gt;
</svg>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Text in SVG</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="300" height="100">
    <!-- Basic text --&gt;
    <text x="10" y="30" font-family="Arial" font-size="24" fill="black">
        Hello SVG!
    </text>
    
    <!-- Text along a path --&gt;
    <path id="curve" d="M 50 80 Q 150 10 250 80" fill="none" />
    <text>
        <textPath href="#curve">
            Text follows this curve!
        </textPath>
    </text>
</svg>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Styling SVG</h2>

                <h3>Inline Attributes</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="200">
    <circle cx="100" cy="100" r="50" 
            fill="blue" 
            stroke="red" 
            stroke-width="3" 
            opacity="0.7" />
</svg>`} />
                </div>

                <h3>CSS Styling</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
.my-circle {
    fill: blue;
    stroke: red;
    stroke-width: 3;
    transition: fill 0.3s;
}

.my-circle:hover {
    fill: green;
    cursor: pointer;
}
</style>

<svg width="200" height="200">
    <circle class="my-circle" cx="100" cy="100" r="50" />
</svg>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Gradients</h2>

                <h3>Linear Gradient</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="300" height="200">
    <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style="stop-color:rgb(255,255,0);stop-opacity:1" />
            <stop offset="100%" style="stop-color:rgb(255,0,0);stop-opacity:1" />
        </linearGradient>
    </defs>
    
    <rect x="10" y="10" width="280" height="180" fill="url(#grad1)" />
</svg>`} />
                </div>

                <h3>Radial Gradient</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="200">
    <defs>
        <radialGradient id="grad2">
            <stop offset="0%" style="stop-color:white" />
            <stop offset="100%" style="stop-color:blue" />
        </radialGradient>
    </defs>
    
    <circle cx="100" cy="100" r="80" fill="url(#grad2)" />
</svg>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Patterns & Filters</h2>

                <h3>Pattern</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="300" height="200">
    <defs>
        <pattern id="dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="3" fill="red" />
        </pattern>
    </defs>
    
    <rect width="300" height="200" fill="url(#dots)" />
</svg>`} />
                </div>

                <h3>Drop Shadow Filter</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="200">
    <defs>
        <filter id="shadow">
            <feDropShadow dx="4" dy="4" stdDeviation="4" flood-opacity="0.5"/>
        </filter>
    </defs>
    
    <circle cx="100" cy="100" r="50" fill="blue" filter="url(#shadow)" />
</svg>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>SVG Animation</h2>

                <h3>CSS Animation</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<style>
@keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.spinning {
    animation: rotate 2s linear infinite;
    transform-origin: center;
}
</style>

<svg width="200" height="200">
    <circle class="spinning" cx="100" cy="100" r="50" fill="blue" />
</svg>`} />
                </div>

                <h3>SMIL Animation (Native SVG)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="200" height="200">
    <circle cx="100" cy="100" r="50" fill="red">
        <animate attributeName="r" 
                 from="50" to="80" 
                 dur="1s" 
                 repeatCount="indefinite" 
                 direction="alternate" />
    </circle>
</svg>`} />
                </div>

                <h3>JavaScript Animation</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg id="my-svg" width="200" height="200">
    <circle id="my-circle" cx="100" cy="100" r="50" fill="green" />
</svg>

<script>
const circle = document.getElementById('my-circle');
let radius = 50;
let growing = true;

function animate() {
    if (growing) {
        radius += 0.5;
        if (radius >= 80) growing = false;
    } else {
        radius -= 0.5;
        if (radius <= 50) growing = true;
    }
    circle.setAttribute('r', radius);
    requestAnimationFrame(animate);
}
animate();
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Using SVG in HTML</h2>

                <h3>1. Inline SVG (Best)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg width="50" height="50">
    <circle cx="25" cy="25" r="20" fill="blue" />
</svg>

<!-- Pros: Full CSS/JS access, no HTTP request --&gt;
<!-- Cons: Increases HTML size --&gt;`} />
                </div>

                <h3>2. External File (img tag)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="icon.svg" alt="Icon" width="50" height="50">

<!-- Pros: Cacheable, clean HTML --&gt;
<!-- Cons: No CSS/JS styling, extra HTTP request --&gt;`} />
                </div>

                <h3>3. CSS Background</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`.icon {
    width: 50px;
    height: 50px;
    background-image: url('icon.svg');
    background-size: contain;
}

<!-- Pros: CSS control, cacheable --&gt;
<!-- Cons: No DOM access --&gt;`} />
                </div>

                <h3>4. Object/Embed</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<object data="graphic.svg" type="image/svg+xml" width="300" height="200">
    <img src="fallback.png" alt="Fallback">
</object>

<!-- Pros: Full SVG features, fallback support --&gt;
<!-- Cons: More complex, extra HTTP request --&gt;`} />
                </div>

                <h3>5. Data URL</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'%3E%3Ccircle cx='25' cy='25' r='20' fill='blue'/%3E%3C/svg%3E" alt="Icon">

<!-- Pros: No HTTP request --&gt;
<!-- Cons: Not cacheable, increases HTML size --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive SVG</h2>

                <h3>Flexible Sizing</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Remove width/height, keep viewBox for flexible sizing --&gt;
<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="40" fill="blue" />
</svg>

<style>
svg {
    width: 100%; /* Scales with container */
    height: auto;
    max-width: 500px;
}
</style>`} />
                </div>

                <h3>Preserve Aspect Ratio</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg viewBox="0 0 100 50" preserveAspectRatio="xMidYMid meet">
    <!-- Content --&gt;
</svg>

<!-- preserveAspectRatio options: --&gt;
<!-- meet: Scale to fit (like background-size: contain) --&gt;
<!-- slice: Scale to fill (like background-size: cover) --&gt;
<!-- xMidYMid: Center horizontally and vertically --&gt;
<!-- none: Stretch to fill --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={`<svg role="img" aria-labelledby="title desc" width="100" height="100">
    <title id="title">Company Logo</title>
    <desc id="desc">A blue circle with white text</desc>
    
    <circle cx="50" cy="50" r="40" fill="blue" />
    <text x="50" y="55" text-anchor="middle" fill="white">Logo</text>
</svg>

<!-- For decorative SVG: --&gt;
<svg aria-hidden="true" focusable="false">
    <!-- Decorative content --&gt;
</svg>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Optimization</h2>

                <h3>Manual Optimization:</h3>
                <ul>
                    <li>Remove unnecessary metadata</li>
                    <li>Simplify paths</li>
                    <li>Combine similar shapes</li>
                    <li>Use symbols for repeated elements</li>
                    <li>Remove hidden/invisible elements</li>
                    <li>Minify (remove whitespace)</li>
                </ul>

                <h3>Tools:</h3>
                <ul>
                    <li><strong>SVGO:</strong> Command-line optimizer</li>
                    <li><strong>SVGOMG:</strong> Web-based interface for SVGO</li>
                    <li><strong>ImageOptim:</strong> Mac app with SVG support</li>
                    <li><strong>Webpack/Vite plugins:</strong> Automatic optimization</li>
                </ul>

                <div className="code-example">
                    <div className="code-header">
                        <span>Before Optimization</span>
                    </div>
                    <CodeBlock language="html" code={`<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
    <!-- Generator: Adobe Illustrator 24.0.0, SVG Export Plug-In ... --&gt;
    <metadata>...</metadata>
    <g id="Layer_1">
        <circle cx="50" cy="50" r="40" fill="#0000FF" />
    </g>
</svg>`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>After Optimization</span>
                    </div>
                    <CodeBlock language="html" code={`<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">
    <circle cx="50" cy="50" r="40" fill="#00f"/>
</svg>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use SVG for logos, icons, illustrations</li>
                            <li>Optimize SVGs before deployment</li>
                            <li>Use viewBox for responsive sizing</li>
                            <li>Inline critical SVGs to avoid HTTP requests</li>
                            <li>Add title and desc for accessibility</li>
                            <li>Use CSS for styling when possible</li>
                            <li>Compress SVG files (gzip)</li>
                            <li>Use symbols for repeated graphics</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use SVG for photographs</li>
                            <li>Inline large/complex SVGs</li>
                            <li>Forget to optimize/minify</li>
                            <li>Use fixed width/height for responsive</li>
                            <li>Leave editor metadata in production</li>
                            <li>Overcomplicate simple graphics</li>
                            <li>Forget accessibility attributes</li>
                            <li>Use SVG when PNG/WebP is better</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLSvg;




